<script setup>
import { computed, onUnmounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useGgFollowupSelection } from 'dashboard/composables/useGgFollowup';
import { useAbortableRequest } from 'dashboard/composables/useAbortableRequest';
import GgFollowupAPI from 'dashboard/api/ggFollowup';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  conversationId: { type: Number, required: true },
  disabled: { type: Boolean, default: false },
});
const { t, locale } = useI18n();
const {
  selection,
  reset,
  setTarget,
  chooseBoundary,
  clearChoice,
  resolveRange,
  setResolving,
} = useGgFollowupSelection();
const request = useAbortableRequest();
const adjustingDisabled = computed(() => props.disabled || selection.resolving);
const instruction = computed(() => {
  if (!selection.first) return t('GG_FOLLOWUP.PICK_FIRST');
  if (selection.target === 'first') return t('GG_FOLLOWUP.PICK_FIRST_BOUNDARY');
  if (selection.target === 'last') return t('GG_FOLLOWUP.PICK_LAST');
  if (selection.choice) return t('GG_FOLLOWUP.CHOOSE_BOUNDARY');
  if (selection.first.id === selection.last.id)
    return t('GG_FOLLOWUP.SINGLE_READY');
  return t('GG_FOLLOWUP.RANGE_READY');
});
const boundaries = computed(() => [
  {
    key: 'first',
    label: t('GG_FOLLOWUP.ADJUST_FIRST'),
    message: selection.first,
  },
  {
    key: 'last',
    label: t('GG_FOLLOWUP.ADJUST_LAST'),
    message: selection.last,
  },
]);
const dateLabel = value =>
  new Intl.DateTimeFormat(locale.value.replace('_', '-'), {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value * 1000));

const loadRange = async () => {
  request.abort();
  const firstId = selection.first?.id;
  const lastId = selection.last?.id;
  if (!firstId || !lastId) return;
  setResolving(true);
  try {
    const response = await request.run(signal =>
      GgFollowupAPI.range(props.conversationId, firstId, lastId, { signal })
    );
    if (!response) return;
    resolveRange(
      response.data.messages.map(message => ({
        id: message.id,
        createdAt: Math.floor(Date.parse(message.created_at) / 1000),
        text: message.text,
        author: message.author || '',
      }))
    );
  } catch (exception) {
    const code = exception.response?.data?.code;
    resolveRange(
      [],
      ['INVALID_RANGE', 'RANGE_TOO_LARGE'].includes(code)
        ? code
        : 'REQUEST_FAILED'
    );
  }
};
watch(
  () => [props.conversationId, selection.first?.id, selection.last?.id],
  loadRange,
  { immediate: true }
);
onUnmounted(() => setResolving(false));
</script>

<template>
  <div
    role="status"
    aria-live="polite"
    class="rounded-lg bg-n-blue-2 p-3 text-sm text-n-blue-11"
  >
    {{ instruction }}
  </div>
  <div v-if="selection.first" class="flex flex-col gap-1" aria-live="polite">
    <span
      v-if="selection.resolving"
      class="flex items-center gap-2 text-xs text-n-slate-11"
    >
      <Spinner class="text-xs" />
      {{ t('GG_FOLLOWUP.COUNTING_RANGE') }}
    </span>
    <template v-else-if="!selection.rangeError">
      <p class="m-0 text-sm font-medium">
        {{
          t('GG_FOLLOWUP.SELECTED_COUNT', {
            count: selection.messages.length,
          })
        }}
      </p>
      <p class="m-0 text-xs text-n-slate-11">
        {{
          t('GG_FOLLOWUP.SELECTED_PERIOD', {
            start: dateLabel(selection.first.createdAt),
            end: dateLabel(selection.last.createdAt),
          })
        }}
      </p>
    </template>
    <template v-else>
      <p role="alert" class="m-0 text-xs text-n-ruby-11">
        {{ t(`GG_FOLLOWUP.ERRORS.${selection.rangeError}`) }}
      </p>
      <Button
        class="self-start"
        :label="t('GG_FOLLOWUP.RETRY')"
        color="slate"
        variant="ghost"
        size="sm"
        :disabled="disabled"
        @click="loadRange"
      />
    </template>
  </div>
  <div
    v-if="selection.choice"
    class="flex flex-col gap-2 rounded-lg border border-n-brand p-3"
  >
    <p class="m-0 text-sm line-clamp-2 break-words">
      {{ selection.choice.text }}
    </p>
    <div class="flex flex-wrap gap-2">
      <Button
        :label="t('GG_FOLLOWUP.START_HERE')"
        color="slate"
        variant="faded"
        size="sm"
        :disabled="adjustingDisabled"
        @click="chooseBoundary('first')"
      />
      <Button
        :label="t('GG_FOLLOWUP.END_HERE')"
        color="slate"
        variant="faded"
        size="sm"
        :disabled="adjustingDisabled"
        @click="chooseBoundary('last')"
      />
      <Button
        :label="t('GG_FOLLOWUP.CANCEL_ADJUSTMENT')"
        color="slate"
        variant="ghost"
        size="sm"
        @click="clearChoice"
      />
    </div>
  </div>
  <div v-if="selection.first" class="flex flex-col gap-2">
    <button
      v-for="boundary in boundaries"
      :key="boundary.key"
      type="button"
      :disabled="adjustingDisabled"
      :aria-pressed="selection.target === boundary.key"
      class="flex flex-col gap-1 w-full rounded-lg border p-2 text-start hover:bg-n-alpha-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand disabled:opacity-50"
      :class="
        selection.target === boundary.key
          ? 'border-n-brand bg-n-blue-2'
          : 'border-n-weak'
      "
      @click="setTarget(boundary.key)"
    >
      <span class="text-xs font-semibold text-n-slate-12">{{
        boundary.label
      }}</span>
      <span class="text-sm line-clamp-2 break-words">{{
        boundary.message.text
      }}</span>
      <span class="text-xs text-n-slate-11">{{
        dateLabel(boundary.message.createdAt)
      }}</span>
    </button>
    <Button
      class="self-start"
      :label="t('GG_FOLLOWUP.RESET')"
      icon="i-lucide-rotate-ccw"
      color="slate"
      variant="ghost"
      size="sm"
      :disabled="disabled"
      @click="reset"
    />
  </div>
  <p class="m-0 text-xs text-n-slate-11">{{ t('GG_FOLLOWUP.SCROLL_HINT') }}</p>
</template>
